import { Injectable, Injector, createNgModule, inject, runInInjectionContext } from '@angular/core';
import { Routes, Route, Router, NavigationStart, ActivationEnd } from '@angular/router';

import { IAscModule, IAscModuleClass } from 'src/modules/i-asc-module';
import { moduleCatalog } from 'src/modules/module-catalog';
import { IAscModuleDefinition } from '../../i-asc-module-definition';
import { FunctionClient } from '../azure-functions';
import { ClientModuleConfiguration } from '../model/client-module-configuraton';
import { INavigationItem } from '../model/i-navigation-item';
import { ModuleSettings } from '../model/module-settings';
import { ClientConfiguration } from '../model/client-configuraton';
import { TelemetryService } from './telemetry.service';
import { EAppInsightsSeverityLevel } from '../model/application-insights';
import { filter } from 'rxjs';
import { NavigationContext } from '../model/navigation-context';
import { CORE_NAVIGATION_ITEMS } from '../core.definition';
import { DesktopService } from './desktop.service';
import { LoggingService } from './logging.service';


@Injectable()
export class ModuleManagerService {
   private injector = inject(Injector);
   private loggingService = inject(LoggingService);
   private telemetryService = inject(TelemetryService);
   private desktopService = inject(DesktopService);


   public isBusy = false;
   public moduleDefinitions: IAscModuleDefinition[] = [];
   public moduleConfigurations: ClientModuleConfiguration[] = [];
   public featureNavigationItems: INavigationItem[] = [];
   public settingsNavigationItems: INavigationItem[] = [];
   private controllerModuleConfiguration!: FunctionClient;
   private controllerModuleSettings!: FunctionClient;
   private modules: IAscModule[] = [];
   private currentNavigationContext?: NavigationContext;

   constructor() {
      const router = inject(Router);

      router.events.pipe(
         filter((event) => { return event instanceof NavigationStart || event instanceof ActivationEnd; })
      ).subscribe(event => { this.handleRouterEvent(event); });
   }


   // -------------------------------------------------------- Routing handling

   private handleRouterEvent(event: NavigationStart | ActivationEnd) {
      if (event instanceof NavigationStart) {
         // Clear current navigation context on new navigation
         this.currentNavigationContext = undefined;
      }
      else if (event instanceof ActivationEnd && !this.currentNavigationContext) {
         // Set navigation context
         this.currentNavigationContext = event.snapshot.data['navigationContext'];
         this.loggingService.logInformation('[Module-Manager]', 'Navigation context changed. Current context is', this.currentNavigationContext);

         // [Desktop only] Forward to first settings panel
         if (this.desktopService.canShowSettingsPanel && this.currentNavigationContext && this.currentNavigationContext.featureNavigationItemId === CORE_NAVIGATION_ITEMS.SETTINGS && !this.currentNavigationContext.settingsNavigationItemId) {
            this.settingsNavigationItems[0].entryPoints[0].navigate();
         }
      }
   }

   // --------------------------------------------------------- Module handling

   public initialize(clientConfiguration: ClientConfiguration) {
      runInInjectionContext(this.injector, () => {
         this.controllerModuleConfiguration = new FunctionClient(clientConfiguration.apiEndpoints.Core_ModuleConfiguration);
         this.controllerModuleSettings = new FunctionClient(clientConfiguration.apiEndpoints.Core_ModuleSettings);
      });
   }

   /**
    * Loads the module configuration for the modules available for the current user.
    */
   public async loadModuleConfigurationAsync(userPrincipalName?: string): Promise<ClientModuleConfiguration[]> {
      try {
         this.isBusy = true;

         let url = this.controllerModuleConfiguration.url;
         if (userPrincipalName) { url += `?userPrincipalName=${userPrincipalName}`; }

         const result = await this.controllerModuleConfiguration.callFunctionGetAsync<ClientModuleConfiguration[]>(url);
         this.moduleConfigurations = (result || []).map(item => ClientModuleConfiguration.parse(item));

         this.loggingService.logInformation('[Module-Manager]', 'Module configuration loaded.');
         return this.moduleConfigurations;
      }
      finally {
         this.isBusy = false;
      }
   }

   /**
    * Loads all modules specified by name.
    */
   public async loadModulesAsync(moduleConfigurations: ClientModuleConfiguration[]): Promise<void> {
      try {
         this.isBusy = true;

         const featureNavigationItems: INavigationItem[] = [];
         const settingsNavigationItems: INavigationItem[] = [];

         // Load modules and get module definitions
         const moduleDefinitionsTasks = (moduleConfigurations || []).map(async moduleConfiguration => {
            try {
               // Check if module is already loaded
               if ((<any>this.modules)[moduleConfiguration.id]) { return; }

               // Get module reference from catalog
               const moduleDefinition: IAscModuleDefinition | undefined = moduleCatalog.find(item => item.moduleId === moduleConfiguration.id);
               if (!moduleDefinition) {
                  throw new Error(`Module '${moduleConfiguration.id}' is undefined.`);
               }

               // Load module
               const moduleClass: IAscModuleClass = await (moduleDefinition.moduleRoute.loadChildren as () => Promise<IAscModuleClass>)();
               const moduleRef = createNgModule(moduleClass, this.injector);
               const module: IAscModule = moduleRef.instance;
               await module.initAsync(moduleConfiguration);
               (<any>this.modules)[moduleConfiguration.id] = module;

               // Get feature navigation items (dashborad & menu)
               module.featureNavigationItems.forEach(item => { featureNavigationItems.push(item); });

               // Get settings navigation items (menu)
               module.settingsNavigationItems.forEach(item => { settingsNavigationItems.push(item); });

               this.loggingService.logInformation('[Module-Manager]', `Module ${moduleConfiguration.id} loaded.`);
            }
            catch (error) {
               const message = `Error loading module '${moduleConfiguration.id}'.`;
               this.loggingService.logError('[Module-Manager]', message, error);
               this.telemetryService.logException(new Error(error as string), undefined, EAppInsightsSeverityLevel.Error, { appName: ModuleManagerService.name });
               //throw new Error(message);
            }
         });

         // Execure tasks
         await Promise.all(moduleDefinitionsTasks);

         this.telemetryService.logTrace(
            'Modules loaded.',
            EAppInsightsSeverityLevel.Information,
            {
               modules: JSON.stringify(Object.keys(this.modules))
            });

         // Sort navigation items
         this.featureNavigationItems = featureNavigationItems.sort((a, b) => (a.sortIndex - b.sortIndex));
         this.settingsNavigationItems = settingsNavigationItems.sort((a, b) => (a.sortIndex - b.sortIndex));
      }
      finally {
         this.isBusy = false;
      }
   }

   public getModuleRoutes(routes: Routes, moduleIDs: string[]): Routes {
      moduleIDs.forEach(moduleID => {
         const moduleDefinition = moduleCatalog.find(item => item.moduleId === moduleID);
         if (moduleDefinition) {
            this.addRoute(routes, moduleDefinition.moduleRoute);
            if (moduleDefinition.settingsRoute) { this.addRoute(routes, moduleDefinition.settingsRoute); }
         }
      });
      return routes;
   }

   private addRoute(routes: Routes, route: Route) {
      if (!route.path) return;

      // Merge child routes (for /settings)
      if (route.path.indexOf('/') > -1) {
         const pathSegment = route.path!.substring(0, route.path!.indexOf('/'));
         const parentRoute = routes.find(r => (r.path === pathSegment));
         if (parentRoute) {
            if (!parentRoute.children) { parentRoute.children = [] as Routes; }

            const childRoute = {
               path: route.path!.substring(route.path!.indexOf('/') + 1),
               loadChildren: route.loadChildren
            };
            this.addRoute(parentRoute.children, childRoute);
            return;
         }
      }

      // Add route
      routes.push(route);
   }

   // --------------------------------------------------------- Module settings

   public getModuleSettings(moduleId: string): ModuleSettings | undefined {
      const moduleConfiguration = this.moduleConfigurations.find(item => item.id === moduleId);
      return (moduleConfiguration) ? moduleConfiguration.settings : undefined;
   }

   public async saveModuleSettingsAsync(settingsTypeDiscriminator?: number): Promise<void> {
      try {
         this.isBusy = true;

         const moduleSettingsList: ModuleSettings[] = [];
         (this.moduleConfigurations || []).forEach(moduleConfiguration => {
            if (moduleConfiguration.settings && (!settingsTypeDiscriminator || settingsTypeDiscriminator === moduleConfiguration.settings.typeDiscriminator)) {
               moduleSettingsList.push(moduleConfiguration.settings);
            }
         });

         const url = this.controllerModuleSettings.url;
         await this.controllerModuleSettings.callFunctionPutAsync<any>(url, moduleSettingsList);
      }
      catch (error) {
         this.loggingService.logError('[Module-Manager]', 'Error while saving module settings.', error);
         throw error;
      }
      finally {
         this.isBusy = false;
      }
   }

   // ------------------------------------------------------ Navigation support

   public isFeatureNavigationItemActive(featureNavigationItemId?: string): boolean {
      return this.currentNavigationContext?.featureNavigationItemId === featureNavigationItemId;
   }

   public isSettingsNavigationItemActive(settingsNavigationItemId?: string): boolean {
      return this.currentNavigationContext?.featureNavigationItemId === CORE_NAVIGATION_ITEMS.SETTINGS
         && this.currentNavigationContext?.settingsNavigationItemId === settingsNavigationItemId;
   }
}
